import { useState, useEffect, useRef } from "react";
import { FilterIcon, Check } from "lucide-react";

const useDropdownPosition = (open) => {
  const dropRef = useRef(null);
  useEffect(() => {
    if (open && dropRef.current) {
      const rect = dropRef.current.getBoundingClientRect();
      const overflows = rect.right > window.innerWidth - 10;
      dropRef.current.style.left = overflows ? "auto" : "0";
      dropRef.current.style.right = overflows ? "0" : "auto";
    }
  }, [open]);
  return dropRef;
};

const ColumnFilterDropdown = ({ column, options, currentValue, defaultVal, onApply, multiSelect = false }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dropRef = useDropdownPosition(open);

  // Parse current value into selected state
  const parseValue = (val) => {
    if (!multiSelect) return val;
    if (Array.isArray(val)) return val;
    if (val && val !== defaultVal) return val.split(",");
    return [];
  };

  const [selected, setSelected] = useState(() => parseValue(currentValue));

  const isActive = multiSelect
    ? (Array.isArray(currentValue) ? currentValue.length > 0 : currentValue !== defaultVal && currentValue !== "")
    : currentValue !== defaultVal;

  useEffect(() => { setSelected(parseValue(currentValue)); }, [currentValue]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const toggleOption = (val) => {
    setSelected((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
  };

  const handleApply = () => {
    onApply(multiSelect ? (selected.length > 0 ? selected.join(",") : defaultVal) : selected);
    setOpen(false);
  };

  const handleClear = () => {
    const reset = multiSelect ? [] : defaultVal;
    setSelected(reset);
    onApply(defaultVal);
    setOpen(false);
  };

  // Filter out "All" option for multi-select
  const displayOptions = multiSelect
    ? options.filter((opt) => (typeof opt === "object" ? opt.value : opt) !== defaultVal)
    : options;

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="ml-1 p-0.5 rounded transition-colors bg-transparent filter-icon-btn"
      >
        <FilterIcon size={12} />
      </button>
      {open && (
        <div
        ref={dropRef}
          className="absolute top-full mt-2 z-[999] normal-case font-normal"
          style={{
            background: "var(--screen-background-color)",
            border: "2px solid rgba(190, 5, 250, 0.2)",
            borderRadius: "10px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            padding: "0",
            minWidth: multiSelect ? "230px" : "220px",
          }}
        >
          {/* Header */}
          <div className="px-3 pt-3 pb-2 flex items-center justify-between">
            <p className="text-xs font-bold text-text-main uppercase tracking-wider m-0">
              {column}
            </p>
            {multiSelect && selected.length > 0 && (
              <span
                className="text-[0.65rem] font-semibold px-1.5 py-0.5 rounded-full"
                style={{ background: "rgba(168, 85, 247, 0.2)", color: "var(--buttons-background-color)" }}
              >
                {selected.length}
              </span>
            )}
          </div>

          {/* Options */}
          <div className="max-h-52 overflow-y-auto">
            {displayOptions.map((opt) => {
              const val = typeof opt === "object" ? opt.value : opt;
              const label = typeof opt === "object" ? opt.label : opt;
              const isSelected = multiSelect ? selected.includes(val) : String(selected) === String(val);

              return (
                <div
                  key={val}
                  onClick={(e) => {
                    e.stopPropagation();
                    multiSelect ? toggleOption(val) : setSelected(val);
                  }}
                  className="cursor-pointer transition-all flex items-center"
                  style={{
                    padding: multiSelect ? "0.5rem 0.75rem" : "0.75rem 1rem",
                    background: isSelected ? "rgba(168, 85, 247, 0.15)" : "transparent",
                    color: isSelected ? "var(--buttons-background-color)" : "var(--text-primary)",
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: "0.85rem",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.background = "rgba(168, 85, 247, 0.08)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isSelected ? "rgba(168, 85, 247, 0.15)" : "transparent";
                  }}
                >
                  {multiSelect && (
                    <span
                      className="flex-shrink-0 flex items-center justify-center mr-2.5"
                      style={{
                        width: "18px",
                        height: "18px",
                        border: isSelected
                          ? "2px solid var(--buttons-background-color)"
                          : "2px solid rgba(190, 5, 250, 0.35)",
                        background: isSelected ? "var(--buttons-background-color)" : "transparent",
                        borderRadius: "4px",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {isSelected && <Check size={12} color="white" strokeWidth={3} />}
                    </span>
                  )}
                  <span className="truncate">{label}</span>
                </div>
              );
            })}
          </div>

          {/* Buttons */}
          <div className="flex gap-2 p-3" style={{ borderTop: "2px solid rgba(190, 5, 250, 0.2)" }}>
            <button
              onClick={handleClear}
              className="flex-1 py-2 rounded-lg text-xs font-medium cursor-pointer transition-all"
              style={{
                border: "2px solid var(--buttons-background-color)",
                color: "var(--text-primary)",
                background: "transparent",
              }}
            >
              Clear
            </button>
            <button
              onClick={handleApply}
              className="flex-1 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all"
              style={{
                background: "var(--buttons-background-color)",
                color: "white",
                border: "2px solid var(--buttons-background-color)",
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </span>
  );
};

export default ColumnFilterDropdown;